import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Home, Smartphone, Bell, Link2Off, ChevronDown, Users2, ShieldCheck, UserMinus, UserPlus, Archive, Trash2, GitMerge, ArrowRight, AlertTriangle } from "lucide-react";
import api, { formatError } from "@/lib/api";
import HouseholdsModal from "./HouseholdsModal";
import ConfirmModal from "./ConfirmModal";

const PLATFORM = { ios: "iOS", android: "Android", windows: "Windows", macos: "macOS", chromeos: "ChromeOS", linux: "Linux", web: "Web" };
const fmtDate = (d) => d ? new Date(d).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

function DevicePanel({ member, onUnlink }) {
  const devs = member.devices || [];
  if (devs.length === 0) return <p className="text-xs text-slate-400 px-1 py-2">Geen gekoppelde apparaten.</p>;
  return (
    <div className="space-y-1.5 mt-2" data-testid={`sa-devices-${member.user_id}`}>
      {devs.map((d) => (
        <div key={d.device_id} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs">
          <Smartphone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-bold text-slate-700">{PLATFORM[d.platform] || d.platform || "Onbekend"}{d.installed ? " · geïnstalleerd" : ""}</p>
            <p className="text-slate-400 truncate">Laatst actief {fmtDate(d.last_seen)}</p>
          </div>
          <button data-testid={`sa-device-unlink-${d.device_id}`} onClick={() => onUnlink(d.device_id, member.name)}
            className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 rounded-md px-2 py-1 font-bold shrink-0">
            <Link2Off className="w-3 h-3" /> Ontkoppel
          </button>
        </div>
      ))}
    </div>
  );
}

function DeleteHouseholdModal({ household, onClose, onDeleted }) {
  const [preview, setPreview] = useState(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/admin/households/${household.org_id}/delete-preview`).then((r) => setPreview(r.data)).catch(() => setPreview({ error: true }));
  }, [household.org_id]);
  const match = typed.trim() === (household.name || "").trim();
  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/households/${household.org_id}`, { params: { confirm_name: typed.trim() } });
      toast.success("Huishouden verwijderd — gebruikersaccounts blijven bestaan");
      onDeleted();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="sa-hh-delete-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-5 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-2 text-rose-700"><Trash2 className="w-5 h-5" /><h3 className="font-head font-bold text-lg">Huishouden verwijderen</h3></div>
        {!preview ? (
          <div className="flex items-center gap-2 text-slate-400 py-6"><Loader2 className="w-5 h-5 animate-spin" /> Controleren…</div>
        ) : preview.error ? (
          <p className="text-sm text-rose-600">Kon de voorbeeldgegevens niet laden.</p>
        ) : (
          <>
            <p className="text-sm text-slate-600">Je staat op het punt <strong>"{preview.name}"</strong> te verwijderen. Dit verwijdert permanent:</p>
            <ul className="text-sm bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 space-y-1">
              <li><strong>{preview.members}</strong> lidmaatschap(pen)</li>
              {Object.entries(preview.data || {}).map(([k, v]) => <li key={k}><strong>{v}</strong> {k}</li>)}
              {Object.keys(preview.data || {}).length === 0 && <li className="text-slate-400">Geen extra data</li>}
            </ul>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-800">
              ✅ <strong>Gebruikersaccounts blijven bestaan.</strong> Ze worden nooit verwijderd — ook niet als ze daarna geen huishouden meer hebben.
            </div>
            {(preview.householdless_users || []).length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800">
                ⚠️ Deze {preview.householdless_users.length} gebruiker(s) hebben hierna <strong>geen huishouden meer</strong> (account blijft bestaan, kunnen opnieuw een huishouden aanmaken/joinen):
                <ul className="mt-1 list-disc list-inside">
                  {preview.householdless_users.map((u, i) => <li key={i}>{u.name} <span className="text-amber-600">({u.email})</span></li>)}
                </ul>
              </div>
            )}
            <div>
              <label className="text-xs font-bold text-slate-500">Typ de huishoudennaam om te bevestigen: <span className="text-slate-700">{preview.name}</span></label>
              <input data-testid="sa-hh-delete-confirm-input" value={typed} onChange={(e) => setTyped(e.target.value)}
                className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-rose-500" placeholder={preview.name} />
            </div>
          </>
        )}
        <div className="flex gap-2 justify-end pt-1">
          <button onClick={onClose} data-testid="sa-hh-delete-cancel" className="px-4 py-2 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-100">Annuleren</button>
          <button onClick={doDelete} disabled={!match || busy} data-testid="sa-hh-delete-confirm"
            className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-40 inline-flex items-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Definitief verwijderen
          </button>
        </div>
      </div>
    </div>
  );
}

function AddMemberRow({ orgId, onAdded }) {
  const [cands, setCands] = useState(null);
  const [sel, setSel] = useState("");
  const [role, setRole] = useState("gebruiker");
  const [busy, setBusy] = useState(false);
  const loadCands = () => api.get(`/admin/households/${orgId}/candidates`).then((r) => setCands(r.data)).catch(() => setCands([]));
  useEffect(() => { loadCands(); }, [orgId]);
  const add = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      await api.post(`/admin/households/${orgId}/members`, { user_id: sel, role });
      toast.success("Lid toegevoegd aan huishouden");
      setSel(""); onAdded();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="p-3.5 bg-indigo-50/50 flex flex-wrap items-center gap-2" data-testid={`sa-hh-add-member-${orgId}`}>
      <UserPlus className="w-4 h-4 text-indigo-500 shrink-0" />
      <span className="text-xs font-bold text-slate-600">Bestaande gebruiker toevoegen:</span>
      <select value={sel} onChange={(e) => setSel(e.target.value)} data-testid={`sa-hh-add-select-${orgId}`}
        className="flex-1 min-w-[180px] border border-slate-300 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-indigo-500 bg-white">
        <option value="">{cands === null ? "Laden…" : cands.length ? "Kies een gebruiker…" : "Geen beschikbare gebruikers"}</option>
        {(cands || []).map((u) => <option key={u.user_id} value={u.user_id}>{u.name}{u.email ? ` (${u.email})` : ""}</option>)}
      </select>
      <select value={role} onChange={(e) => setRole(e.target.value)} data-testid={`sa-hh-add-role-${orgId}`}
        className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-indigo-500 bg-white">
        <option value="gebruiker">Gebruiker</option>
        <option value="beheerder">Beheerder</option>
      </select>
      <button onClick={add} disabled={!sel || busy} data-testid={`sa-hh-add-btn-${orgId}`}
        className="inline-flex items-center gap-1 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 rounded-lg px-3 py-1.5">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />} Toevoegen
      </button>
    </div>
  );
}

function MergeWizard({ rows, onClose, onDone }) {
  const active = rows.filter((h) => !h.archived);
  const [step, setStep] = useState(1);
  const [source, setSource] = useState("");
  const [target, setTarget] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingP, setLoadingP] = useState(false);
  const [nameChoice, setNameChoice] = useState("target");
  const [customName, setCustomName] = useState("");
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const goPreview = async () => {
    if (!source || !target || source === target) return;
    setLoadingP(true);
    try {
      const r = await api.get("/admin/households/merge-preview", { params: { source, target } });
      setPreview(r.data);
      setNameChoice("target");
      setCustomName(r.data.target.name || "");
      setStep(2);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setLoadingP(false); }
  };

  const keepName = nameChoice === "source" ? preview?.source.name
    : nameChoice === "target" ? preview?.target.name : customName.trim();
  const match = typed.trim() === (preview?.source.name || "").trim();

  const doMerge = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/households/merge", {
        source_org_id: source, target_org_id: target, keep_name: keepName, confirm_name: typed.trim(),
      });
      toast.success(`Samengevoegd tot "${r.data.target}" — ${r.data.members_moved} lid verhuisd, ${r.data.members_merged} samengevoegd`);
      onDone();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="sa-merge-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-5 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-2 text-indigo-700">
          <GitMerge className="w-5 h-5" /><h3 className="font-head font-bold text-lg">Huishoudens samenvoegen</h3>
          <span className="ml-auto text-xs font-bold text-slate-400">Stap {step} van 3</span>
        </div>

        {step === 1 && (
          <div className="space-y-4" data-testid="sa-merge-step1">
            <p className="text-sm text-slate-600">Kies welk huishouden wordt <strong>opgeheven</strong> (bron) en waar alles naartoe verhuist (doel). Gebruikersaccounts blijven altijd bestaan.</p>
            <div>
              <label className="text-xs font-bold text-slate-500">Bron — wordt opgeheven</label>
              <select data-testid="sa-merge-source" value={source} onChange={(e) => setSource(e.target.value)}
                className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-500">
                <option value="">Kies huishouden…</option>
                {active.map((h) => <option key={h.org_id} value={h.org_id} disabled={h.org_id === target}>{h.name} · {h.member_count} leden</option>)}
              </select>
            </div>
            <div className="flex justify-center text-slate-400"><ArrowRight className="w-5 h-5 rotate-90" /></div>
            <div>
              <label className="text-xs font-bold text-slate-500">Doel — blijft bestaan</label>
              <select data-testid="sa-merge-target" value={target} onChange={(e) => setTarget(e.target.value)}
                className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-500">
                <option value="">Kies huishouden…</option>
                {active.map((h) => <option key={h.org_id} value={h.org_id} disabled={h.org_id === source}>{h.name} · {h.member_count} leden</option>)}
              </select>
            </div>
          </div>
        )}

        {step === 2 && preview && (
          <div className="space-y-4" data-testid="sa-merge-step2">
            <div className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
              <strong>{preview.source.name}</strong><ArrowRight className="w-4 h-4 text-slate-400" /><strong>{preview.target.name}</strong>
            </div>
            <ul className="text-sm bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 space-y-1">
              <li><strong>{preview.moved.length}</strong> lid(eren) verhuizen mee</li>
              {preview.overlap.length > 0 && <li><strong>{preview.overlap.length}</strong> lid(eren) zitten al in het doel (worden samengevoegd)</li>}
              {Object.entries(preview.data || {}).map(([k, v]) => <li key={k}><strong>{v}</strong> {k}</li>)}
              <li className="text-slate-500 pt-1">Na samenvoegen: <strong>{preview.final_member_count}</strong> leden in totaal</li>
            </ul>
            <div>
              <label className="text-xs font-bold text-slate-500">Welke naam wil je behouden?</label>
              <div className="mt-1 space-y-1.5">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" data-testid="sa-merge-name-target" checked={nameChoice === "target"} onChange={() => setNameChoice("target")} />
                  <span>Doelnaam: <strong>{preview.target.name}</strong></span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" data-testid="sa-merge-name-source" checked={nameChoice === "source"} onChange={() => setNameChoice("source")} />
                  <span>Bronnaam: <strong>{preview.source.name}</strong></span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" data-testid="sa-merge-name-custom" checked={nameChoice === "custom"} onChange={() => setNameChoice("custom")} />
                  <span>Eigen naam:</span>
                  <input data-testid="sa-merge-name-custom-input" value={customName} onFocus={() => setNameChoice("custom")}
                    onChange={(e) => setCustomName(e.target.value)} placeholder="Nieuwe naam"
                    className="flex-1 border border-slate-300 rounded-lg px-2 py-1 text-sm outline-none focus:border-indigo-500" />
                </label>
              </div>
            </div>
          </div>
        )}

        {step === 3 && preview && (
          <div className="space-y-4" data-testid="sa-merge-step3">
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800">
              ⚠️ <strong>{preview.source.name}</strong> wordt opgeheven. Alle data en leden verhuizen naar het samengevoegde huishouden dat verdergaat als <strong>"{keepName}"</strong>. Dit kan niet ongedaan worden gemaakt.
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500">Typ de naam van het op te heffen huishouden om te bevestigen: <span className="text-slate-700">{preview.source.name}</span></label>
              <input data-testid="sa-merge-confirm-input" value={typed} onChange={(e) => setTyped(e.target.value)}
                className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-500" placeholder={preview.source.name} />
            </div>
          </div>
        )}

        <div className="flex gap-2 justify-end pt-1">
          <button onClick={onClose} data-testid="sa-merge-cancel" className="px-4 py-2 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-100">Annuleren</button>
          {step > 1 && <button onClick={() => setStep(step - 1)} data-testid="sa-merge-back" className="px-4 py-2 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-100">Terug</button>}
          {step === 1 && (
            <button onClick={goPreview} disabled={!source || !target || source === target || loadingP} data-testid="sa-merge-next-1"
              className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 inline-flex items-center gap-2">
              {loadingP && <Loader2 className="w-4 h-4 animate-spin" />} Volgende
            </button>
          )}
          {step === 2 && (
            <button onClick={() => setStep(3)} disabled={!keepName} data-testid="sa-merge-next-2"
              className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40">Volgende</button>
          )}
          {step === 3 && (
            <button onClick={doMerge} disabled={!match || busy} data-testid="sa-merge-confirm"
              className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 inline-flex items-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Samenvoegen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Households() {
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [expandDevices, setExpandDevices] = useState({});
  const [hhModal, setHhModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [defOrg, setDefOrg] = useState(null);

  const load = () => api.get("/admin/households").then((r) => setRows(r.data)).catch(() => setRows([]));
  const loadDef = () => api.get("/admin/default-org").then((r) => setDefOrg(r.data)).catch(() => setDefOrg(null));
  useEffect(() => { load(); loadDef(); }, []);

  const run = async (fn, okMsg) => {
    setConfirm(null);
    try { await fn(); toast.success(okMsg); await load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const unlink = (deviceId, name) => setConfirm({
    title: "Apparaat ontkoppelen?", message: `Het apparaat van ${name} wordt losgekoppeld en moet opnieuw inloggen.`,
    confirmLabel: "Ontkoppelen", onConfirm: () => run(() => api.delete(`/admin/devices/${deviceId}`), "Apparaat ontkoppeld"),
  });
  const askRemoveMember = (orgId, m) => setConfirm({
    title: "Lid verwijderen?", message: `${m.name} wordt uit dit huishouden verwijderd en krijgt een e-mail. Het account blijft bestaan.`,
    confirmLabel: "Verwijderen", onConfirm: () => run(() => api.delete(`/admin/households/${orgId}/members/${m.user_id}`), "Lid verwijderd"),
  });
  const askArchive = (h) => setConfirm({
    title: "Huishouden archiveren?", message: `"${h.name}" wordt gearchiveerd en is niet meer toegankelijk voor de leden. Alle leden krijgen een e-mail. De data blijft bewaard.`,
    confirmLabel: "Archiveren", onConfirm: () => run(() => api.post(`/admin/households/${h.org_id}/archive`), "Huishouden gearchiveerd"),
  });
  const askDelete = (h) => setDelTarget(h);
  const askUnlinkDefault = () => setConfirm({
    title: "Standaard-org loskoppelen?",
    message: "De standaard-organisatie gaat naar een leeg, verborgen systeem-huishouden wijzen. Bestaande huishoudens en leden veranderen niet. Dit voorkomt dat de opstart-migratie ooit nog verweesde accounts aan een echt huishouden toevoegt.",
    confirmLabel: "Loskoppelen",
    onConfirm: () => run(async () => { await api.post("/admin/default-org/unlink"); await loadDef(); }, "Standaard-org losgekoppeld"),
  });

  if (!rows) return <div className="flex items-center gap-2 text-slate-400 py-12"><Loader2 className="w-5 h-5 animate-spin" /> Laden…</div>;
  if (rows.length === 0) return <p className="text-slate-400 py-12">Nog geen huishoudens.</p>;

  return (
    <div className="space-y-4" data-testid="sa-households">
      {defOrg && (
        <div className={`rounded-2xl border p-4 ${defOrg.is_real_household ? "bg-amber-50 border-amber-300" : "bg-slate-50 border-slate-200"}`} data-testid="sa-default-org">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${defOrg.is_real_household ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
              {defOrg.is_real_household ? <AlertTriangle className="w-5 h-5" strokeWidth={2.5} /> : <ShieldCheck className="w-5 h-5" strokeWidth={2.5} />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-head font-black leading-tight">Standaard-organisatie</p>
              <p className="text-sm text-slate-600">
                {defOrg.exists
                  ? <>Wijst nu naar <strong data-testid="sa-default-org-name">{defOrg.name}</strong> ({defOrg.member_count} leden{defOrg.data_count ? `, ${defOrg.data_count} items` : ""}).</>
                  : "Niet ingesteld."}
              </p>
              {defOrg.is_real_household ? (
                <p className="text-sm text-amber-800 mt-1 font-semibold">⚠️ Dit is een écht huishouden. Bij een herstart kunnen verweesde accounts hierin belanden. Koppel los naar een leeg systeem-huishouden.</p>
              ) : (
                <p className="text-sm text-emerald-700 mt-1 font-semibold" data-testid="sa-default-org-safe">✅ Veilig — dit wijst niet naar een echt huishouden.</p>
              )}
            </div>
            {defOrg.is_real_household && (
              <button data-testid="sa-default-org-unlink" onClick={askUnlinkDefault}
                className="inline-flex items-center gap-1.5 text-sm font-bold text-amber-800 bg-white border border-amber-300 hover:bg-amber-100 rounded-lg px-3 py-2 shrink-0">
                <Link2Off className="w-4 h-4" />Loskoppelen
              </button>
            )}
          </div>
        </div>
      )}
      {rows.filter((h) => !h.archived).length >= 2 && (
        <div className="flex justify-end">
          <button data-testid="sa-merge-open" onClick={() => setMergeOpen(true)}
            className="inline-flex items-center gap-1.5 text-sm font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 rounded-lg px-3 py-2">
            <GitMerge className="w-4 h-4" />Huishoudens samenvoegen
          </button>
        </div>
      )}
      {rows.map((h) => (
        <div key={h.org_id} className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid={`sa-household-${h.org_id}`}>
          <div className={`w-full flex items-center gap-3 p-4 ${h.archived ? "opacity-60" : ""}`}>
            <button onClick={() => setOpenId(openId === h.org_id ? null : h.org_id)} className="flex items-center gap-3 text-left flex-1 min-w-0 hover:opacity-80">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0"><Home className="w-5 h-5" strokeWidth={2.5} /></div>
              <div className="min-w-0 flex-1">
                <p className="font-head font-black leading-tight truncate flex items-center gap-2">{h.name}
                  {h.archived && <span className="text-[10px] font-bold text-slate-500 bg-slate-200 rounded-full px-2 py-0.5" data-testid={`sa-hh-archived-${h.org_id}`}>Gearchiveerd</span>}
                </p>
                <p className="text-xs text-slate-400">{h.type || "huishouden"} · <span className="font-mono">{h.org_id}</span></p>
              </div>
            </button>
            <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 bg-slate-100 rounded-full px-2.5 py-1"><Users2 className="w-3.5 h-3.5" />{h.member_count}</span>
            {!h.archived && (
              <button data-testid={`sa-hh-archive-${h.org_id}`} onClick={() => askArchive(h)} title="Huishouden archiveren"
                className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 rounded-lg px-2 py-1.5">
                <Archive className="w-3.5 h-3.5" />Archiveren
              </button>
            )}
            <button data-testid={`sa-hh-delete-${h.org_id}`} onClick={() => askDelete(h)} title="Huishouden definitief verwijderen"
              className="inline-flex items-center gap-1 text-xs font-bold text-rose-700 bg-rose-50 border border-rose-200 hover:bg-rose-100 rounded-lg px-2 py-1.5">
              <Trash2 className="w-3.5 h-3.5" />Verwijder
            </button>
            <button onClick={() => setOpenId(openId === h.org_id ? null : h.org_id)} className="p-1">
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${openId === h.org_id ? "rotate-180" : ""}`} />
            </button>
          </div>
          {openId === h.org_id && (
            <div className="border-t border-slate-100 divide-y divide-slate-100">
              {h.members.map((m) => (
                <div key={m.user_id} className="p-3.5" data-testid={`sa-hh-member-${m.user_id}`}>
                  <div className="flex items-center gap-3">
                    {m.avatar_url
                      ? <img src={m.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
                      : <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center font-head font-black text-slate-500 shrink-0">{m.name?.[0]?.toUpperCase()}</div>}
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-sm flex items-center gap-1.5 truncate">
                        {m.name}
                        {m.role === "beheerder" && <ShieldCheck className="w-3.5 h-3.5 text-indigo-500 shrink-0" title="Beheerder" />}
                      </p>
                      <p className="text-xs text-slate-400 truncate">{m.email || "PIN-gebruiker"}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {m.other_households > 0 && (
                        <button data-testid={`sa-hh-multi-${m.user_id}`} onClick={() => setHhModal(m)} title="Bekijk alle huishoudens"
                          className="text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 rounded-full px-2 py-0.5">
                          +{m.other_households} huish.
                        </button>
                      )}
                      <span className={`inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-0.5 ${m.installed_count > 0 ? "text-emerald-700 bg-emerald-50 border border-emerald-200" : m.device_count > 0 ? "text-slate-600 bg-slate-100" : "text-slate-400 bg-slate-50 border border-slate-200"}`}>
                        <Smartphone className="w-3 h-3" />{m.installed_count > 0 ? `${m.installed_count} geïnst.` : m.device_count > 0 ? `${m.device_count} app.` : "geen app"}
                      </span>
                      {m.push_count > 0 && <span className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2 py-0.5" title="Notificaties aan"><Bell className="w-3 h-3" />{m.push_count}</span>}
                      {m.device_count > 0 && (
                        <button data-testid={`sa-hh-toggle-devices-${m.user_id}`} onClick={() => setExpandDevices((s) => ({ ...s, [m.user_id]: !s[m.user_id] }))}
                          className="text-xs text-indigo-600 font-bold underline underline-offset-2">{expandDevices[m.user_id] ? "verberg" : "toon"}</button>
                      )}
                      {!h.archived && (
                        <button data-testid={`sa-hh-remove-${m.user_id}`} onClick={() => askRemoveMember(h.org_id, m)} title="Lid verwijderen uit dit huishouden"
                          className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-700 bg-rose-50 border border-rose-200 hover:bg-rose-100 rounded-full px-2 py-0.5">
                          <UserMinus className="w-3 h-3" />Verwijder
                        </button>
                      )}
                    </div>
                  </div>
                  {expandDevices[m.user_id] && <DevicePanel member={m} onUnlink={unlink} />}
                </div>
              ))}
              {!h.archived && <AddMemberRow orgId={h.org_id} onAdded={load} />}
            </div>
          )}
        </div>
      ))}
      <HouseholdsModal user={hhModal} onClose={() => setHhModal(null)} currentOrgId={openId} />
      <ConfirmModal open={!!confirm} title={confirm?.title} message={confirm?.message}
        confirmLabel={confirm?.confirmLabel} onConfirm={confirm?.onConfirm} onCancel={() => setConfirm(null)} />
      {delTarget && (
        <DeleteHouseholdModal household={delTarget}
          onClose={() => setDelTarget(null)}
          onDeleted={() => { if (openId === delTarget.org_id) setOpenId(null); setDelTarget(null); load(); }} />
      )}
      {mergeOpen && (
        <MergeWizard rows={rows} onClose={() => setMergeOpen(false)}
          onDone={() => { setMergeOpen(false); setOpenId(null); load(); }} />
      )}
    </div>
  );
}
